"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import DocsPageFrame from "../../../components/DocsPageFrame";
import {
  BarChart3,
  Users,
  Megaphone,
  MapPin,
  Layers,
  Landmark,
  ArrowRight,
  CheckCircle,
  Zap,
  Link2,
} from "lucide-react";
import Link from "next/link";

const MODULES = [
  {
    icon: Users,
    color: "#DF5D01",
    bg: "#FFF7F2",
    name: "Lead Management",
    desc: "Capture, qualify, and track leads from first contact to a closed deal. Bulk import from spreadsheets, log activities, and keep a full change history on every lead.",
    links: [
      { label: "Leads", href: "/documentation/crm/sales/leads" },
      {
        label: "Lead Status",
        href: "/documentation/crm/all-master/lead-status",
      },
      {
        label: "Lead Source",
        href: "/documentation/crm/all-master/lead-source",
      },
      { label: "Campaigns", href: "/documentation/crm/sales/campaigns" },
    ],
  },
  {
    icon: Layers,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Sales",
    desc: "Turn leads into GST-compliant quotations, then convert accepted quotes into confirmed sales orders that flow straight into ABSS Books - no re-entry.",
    links: [
      { label: "Quotations", href: "/documentation/crm/sales/quotations" },
      { label: "Sales Orders", href: "/documentation/crm/sales/sales-orders" },
      {
        label: "Sales Order Tag",
        href: "/documentation/crm/all-master/sales-order-tag",
      },
    ],
  },
  {
    icon: MapPin,
    color: "#7C3AED",
    bg: "#F5F3FF",
    name: "Field Activity",
    desc: "Record on-site visits with check-in / check-out sessions and outcomes, and assign follow-up tasks with due dates so nothing about a prospect slips.",
    links: [
      { label: "Visit", href: "/documentation/crm/sales/visit" },
      { label: "Task", href: "/documentation/crm/sales/task" },
    ],
  },
  {
    icon: Megaphone,
    color: "#059669",
    bg: "#ECFDF5",
    name: "Masters",
    desc: "Customers, items, item groups, industries, and tags - the shared data that powers every quote and order. Configured once, used everywhere.",
    links: [
      { label: "Customer", href: "/documentation/crm/all-master/customer" },
      { label: "Item", href: "/documentation/crm/all-master/item" },
      { label: "Item Group", href: "/documentation/crm/all-master/item-group" },
      {
        label: "Lead Industry",
        href: "/documentation/crm/all-master/lead-industry",
      },
    ],
  },
  {
    icon: Landmark,
    color: "#DF5D01",
    bg: "#FFF7F2",
    name: "Banking",
    desc: "Maintain your company's bank accounts and choose a primary account whose details print on your quotations, sales orders, and invoices.",
    links: [{ label: "Bank", href: "/documentation/crm/banking/bank" }],
  },
  {
    icon: BarChart3,
    color: "#1785B6",
    bg: "#EFF8FF",
    name: "Dashboard & Reports",
    desc: "A live dashboard summarising leads, quotes, and sales, plus ready-made reports for campaigns, quotations, orders, and team performance.",
    links: [
      { label: "Dashboard", href: "/documentation/crm/home/dashboard" },
      { label: "Reports", href: "/documentation/crm/reports/reports" },
    ],
  },
];

const KEY_FEATURES = [
  "India-first design: GST-ready quotes and orders that hand off cleanly to ABSS Books",
  "QR-code lead capture at trade shows and via web forms",
  "Bulk import for leads, customers, and items from spreadsheets",
  "Manual lead assignment to sales reps, with state-level analytics and reporting",
  "Full activity timeline and change history on every lead",
  "Role-based access control for sales reps, managers, and admins",
  "Real-time data shared with ABSS Books - one customer and item master",
  "Mobile-responsive web app - works on any device, no app install needed",
];

const BOOKS_FLOW = [
  {
    label: "Quote → Sales Order",
    desc: "An accepted quotation converts to a sales order in one step",
  },
  {
    label: "Sales Order → Invoice",
    desc: "The order flows into ABSS Books for delivery and invoicing",
  },
  {
    label: "Shared Customer Master",
    desc: "The same customer record is used across CRM and Books",
  },
  {
    label: "Shared Item Master",
    desc: "Pricing, HSN codes, and GST rates stay in sync automatically",
  },
  {
    label: "Payment Status Sync",
    desc: "Payments recorded in Books reflect on the customer in CRM",
  },
  {
    label: "One Login",
    desc: "Sales and accounts teams work from the same live data",
  },
];

const sections = [
  { heading: "Who uses ABSS CRM?" },
  { heading: "Core modules" },
  { heading: "Key capabilities" },
  { heading: "How ABSS CRM connects with ABSS Books" },
  { heading: "Getting started" },
];

export default function AboutCrmPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.1 }}
        variants={stagger}
      >
        {/* Header */}
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[32px] sm:text-[40px] md:text-[46px] leading-[1.15] text-[#171717] sentient-medium mb-5">
            About ABSS CRM
          </h1>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#525252] geist-regular max-w-2xl">
            ABSS CRM is a complete sales and lead-management platform built for
            Indian businesses. It handles the full sales lifecycle - from
            capturing a lead and running campaigns, through visits, tasks, and
            quotations, to confirmed sales orders that flow straight into ABSS
            Books - in one integrated system.
          </p>
        </motion.div>

        {/* Who it is for */}
        <motion.div
          variants={fadeUp}
          className="mb-12 p-6 bg-[#F9F9F9] border border-[#E5E5E5] rounded-[14px]"
        >
          <h2
            id="section-0"
            className="text-[18px] sm:text-[20px] sentient-medium text-[#171717] mb-3"
          >
            Who uses ABSS CRM?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              {
                role: "Field sales teams",
                detail: "Logging visits, tasks, and follow-ups on the go",
              },
              {
                role: "Sales managers",
                detail: "Tracking pipeline, targets, and team performance",
              },
              {
                role: "Manufacturers & traders",
                detail: "Quoting and confirming orders that feed into Books",
              },
              {
                role: "Marketing teams",
                detail: "Running campaigns and measuring lead attribution",
              },
              {
                role: "Distributors",
                detail: "Managing leads and orders across regions",
              },
              {
                role: "Event-driven businesses",
                detail: "Capturing leads at trade shows via QR codes",
              },
            ].map((item) => (
              <div key={item.role} className="flex items-start gap-2.5">
                <CheckCircle className="w-4 h-4 text-[#DF5D01] mt-0.5 flex-shrink-0" />
                <div>
                  <span className="text-[13px] geist-medium text-[#171717]">
                    {item.role}
                  </span>
                  <span className="text-[13px] geist-regular text-[#525252]">
                    {" "}
                    - {item.detail}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Modules */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-2"
          >
            Core modules
          </h2>
          <p className="text-[14px] sm:text-[15px] geist-regular text-[#525252] mb-6">
            ABSS CRM is organised into modules. Each module has its own
            documentation section accessible from the sidebar.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {MODULES.map((mod) => {
              const Icon = mod.icon;
              return (
                <div
                  key={mod.name}
                  className="border border-[#E5E5E5] rounded-[14px] p-5 bg-white"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-9 h-9 rounded-[8px] flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: mod.bg }}
                    >
                      <Icon
                        className="w-4.5 h-4.5"
                        style={{ color: mod.color }}
                      />
                    </div>
                    <h3 className="text-[16px] sentient-medium text-[#171717]">
                      {mod.name}
                    </h3>
                  </div>
                  <p className="text-[13px] geist-regular text-[#525252] leading-relaxed mb-3">
                    {mod.desc}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {mod.links.map((link) => (
                      <Link
                        key={link.label}
                        href={link.href}
                        className="text-[12px] geist-medium text-[#525252] bg-[#F5F5F5] hover:bg-[#EBEBEB] border border-[#E5E5E5] rounded-[6px] px-2.5 py-1 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Key features */}
        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-2"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-6"
          >
            Key capabilities
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
            {KEY_FEATURES.map((f, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] mt-2.5 flex-shrink-0" />
                <p className="text-[14px] geist-regular text-[#525252] leading-relaxed">
                  {f}
                </p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Books connection */}
        <motion.div
          variants={fadeUp}
          className="mb-12 p-6 border border-[#DBEAFE] bg-[#EFF8FF] rounded-[14px]"
        >
          <div className="flex items-center gap-2 mb-3">
            <Link2 className="w-4 h-4 text-[#1E40AF]" />
            <h2
              id="section-3"
              className="text-[16px] sm:text-[18px] sentient-medium text-[#1E40AF]"
            >
              How ABSS CRM connects with ABSS Books
            </h2>
          </div>
          <p className="text-[14px] geist-regular text-[#1E40AF] leading-relaxed mb-5">
            ABSS CRM and ABSS Books share a single database. When a deal is won
            in CRM, the customer record, pricing, and line items flow directly
            into Books - no re-entry. Payments recorded in Books update the
            customer&apos;s status in CRM automatically.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {BOOKS_FLOW.map((item) => (
              <div
                key={item.label}
                className="border border-[#DBEAFE] rounded-[12px] p-4 bg-white flex items-start gap-3"
              >
                <ArrowRight className="w-4 h-4 text-[#1785B6] mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[14px] geist-medium text-[#171717] mb-0.5">
                    {item.label}
                  </p>
                  <p className="text-[12px] geist-regular text-[#525252] leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Getting started CTA */}
        <motion.div variants={fadeUp} className="mb-0">
          <h2
            id="section-4"
            className="text-[22px] sm:text-[26px] sentient-medium text-[#171717] mb-6"
          >
            Getting started
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link
              href="/documentation/crm/sales/leads"
              className="group border border-[#E5E5E5] hover:border-black rounded-[14px] p-5 bg-white transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-4 h-4 text-[#DF5D01]" />
                <span className="text-[15px] sentient-medium text-[#171717] transition-colors">
                  Start with Leads
                </span>
              </div>
              <p className="text-[13px] geist-regular text-[#525252]">
                Learn how to capture, assign, and track leads from first contact
                to a won deal.
              </p>
              <div className="flex items-center gap-1 mt-3 text-[13px] geist-medium text-[#DF5D01]">
                Open the guide <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
            <Link
              href="/documentation/crm/home/dashboard"
              className="group border border-[#E5E5E5] hover:border-black rounded-[14px] p-5 bg-white transition-all"
            >
              <div className="flex items-center gap-3 mb-2">
                <Zap className="w-4 h-4 text-[#1785B6]" />
                <span className="text-[15px] sentient-medium text-[#171717] transition-colors">
                  Explore the Dashboard
                </span>
              </div>
              <p className="text-[13px] geist-regular text-[#525252]">
                See how the live dashboard summarises your leads, quotes,
                orders, and team activity.
              </p>
              <div className="flex items-center gap-1 mt-3 text-[13px] geist-medium text-[#1785B6]">
                View dashboard <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
